import React from 'react';
import { css } from 'styled-components';
import { SelectPicker } from 'rsuite';
import Icons from '@/common/view/icons';
import colors from '@/common/view/colors';
import { Typescales } from '@/common/view/typescales';
import styled, { createGlobalStyle } from 'styled-components';

export const GlobalSelectStyle = createGlobalStyle`
  :root {
    --rs-input-focus-border: ${colors.grayBlack};
    --rs-input-disabled-bg: ${colors.gray10};
    --rs-listbox-option-hover-bg: ${colors.blue20};
    --rs-listbox-option-hover-text: ${colors.blue50};
    --rs-listbox-option-selected-bg: ${colors.blue10};
    --rs-listbox-option-selected-text: ${colors.blue50};
    --rs-text-primary: ${colors.gray100};
    --rs-state-error: ${colors.red50};
  }
  .rs-picker-select-menu-item {
    ${({ theme }) => theme.typescales.body4};
    &.rs-picker-select-menu-item-active {
      
    }
  }
  
  .rs-picker-select-menu.rs-picker-popup {
    transform: translate(0, 0);
    z-index: 1070;
    .rs-picker-search-bar {
      input.rs-picker-search-bar-input {
        padding: 7px 11px 7px 36px;
        &:focus {
          box-shadow: inset 0 0 0 1px var(--rs-input-focus-border);
          outline: none;
        }
      }
    }
    .rs-picker-search-bar-search-icon {
      left: 24px;
      right: auto;
    }

    .rs-input-group:focus-within, .rs-input-group:not(.rs-input-group-disabled).rs-input-group-focus, .rs-input-group:not(.rs-input-group-disabled):hover {
      outline: 2px solid ${({ theme }) => theme.colors.blue50};
      border-color: transparent;
    }
    .rs-search-box-input {
      ${({ theme }) => theme.typescales.body4};
    }
    .rs-picker-none {
      padding: 8px 12px;
      ${({ theme }) => theme.typescales.body4};
    }
  }
`;

type SelectSize = 'xsmall' | 'small' | 'medium';

const sizes: Record<
  SelectSize,
  {
    padding: string;
    typescale: Typescales;
    label?: {
      topSpacing: string;
    };
    caret: {
      width: string;
      height: string;
      top: string;
    };
    cleanBtn: {
      width: string;
      height: string;
      top: string;
    };
  }
> = {
  xsmall: {
    padding: '6px 38px 6px 12px',
    typescale: 'caption1',
    label: {
      topSpacing: '1px',
    },
    caret: {
      width: '14px',
      height: '14px',
      top: '8px',
    },
    cleanBtn: {
      width: '12px',
      height: '12px',
      top: '5px',
    },
  },
  small: {
    padding: '10px 38px 10px 12px',
    typescale: 'body4',
    caret: {
      width: '18px',
      height: '18px',
      top: '11px',
    },
    cleanBtn: {
      width: '14px',
      height: '14px',
      top: '10px',
    },
  },
  medium: {
    padding: '10px 38px 10px 12px',
    typescale: 'body1',
    caret: {
      width: '20px',
      height: '20px',
      top: '12px',
    },
    cleanBtn: {
      width: '16px',
      height: '16px',
      top: '12px',
    },
  },
} as const;

export type SelectStyleProps = {
  $size: SelectSize;
  $width?: string;
  $error?: boolean;
};

export const selectStyles = css<SelectStyleProps>`
  min-width: 120px;
  width: ${({ $width }) => $width || 'auto'};
  &.rs-picker.rs-picker-default {
    .rs-btn-default.rs-picker-toggle {
      border: none;
      border-radius: 8px;
      padding: ${({ $size }) => sizes[$size].padding};
      box-shadow: inset 0 0 0 1px ${({ $error, theme }) => ($error ? theme.colors.red50 : theme.colors.gray30)};
      &:hover {
        box-shadow: inset 0 0 0 1px ${({ $error, theme }) => ($error ? theme.colors.red50 : theme.colors.grayBlack)};
      }
      &:focus {
        box-shadow: inset 0 0 0 2px ${({ $error, theme }) => ($error ? theme.colors.red50 : theme.colors.grayBlack)};
      }
      * {
        ${({ $size, theme }) => theme.typescales[sizes[$size].typescale]};
      }
    }

    &.rs-picker-read-only {
      opacity: 1;
      .rs-btn-default.rs-picker-toggle {
        cursor: default;
        &:hover {
          box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.gray30};
        }
        &:focus {
          box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.gray30};
        }
        svg * {
          fill: ${({ theme }) => theme.colors.gray40};
        }
      }
    }
    &.rs-picker-disabled {
      opacity: 1;
      .rs-btn-default.rs-picker-toggle {
        color: ${({ theme }) => theme.colors.gray50};
        &:hover {
          box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.gray30};
        }
        svg * {
          fill: ${({ theme }) => theme.colors.gray40};
        }
      }
    }

    .rs-stack {
      ${({ theme }) => theme.typescales.body4};
      .rs-stack-item {
        .rs-loader-wrapper {
          height: 20px;
        }
        span.rs-picker-toggle-label,
        span.rs-picker-toggle-placeholder,
        span.rs-picker-toggle-value {
          display: block;
          ${({ $size }) => {
            const topSpacing = sizes[$size].label?.topSpacing;
            return topSpacing ? `transform: translateY(${topSpacing});` : '';
          }};
        }
        input.rs-picker-toggle-textbox {
          padding: 0;
        }
        svg.rs-picker-caret-icon {
          ${({ $size }) => {
            const caret = sizes[$size].caret;
            return `
              width: ${caret.width};
              height: ${caret.height};
              top: ${caret.top};
            `;
          }}
        }

        button.rs-picker-clean {
          ${({ $size }) => {
            const cleanBtn = sizes[$size].cleanBtn;
            return `
              top: ${cleanBtn.top};
              svg {
                width: ${cleanBtn.width};
                height: ${cleanBtn.height};
              }
            `;
          }}
          &:hover {
            svg * {
              stroke: ${({ theme }) => theme.colors.grayBlack};
            }
          }
        }
        span.rs-picker-toggle-value {
          color: ${({ theme }) => theme.colors.grayBlack};
        }
      }
    }

    .rs-loader-wrapper {
      padding: 0;
      height: ${({ $size }) => ($size === 'small' ? '20px' : '18px')}!important;
      .rs-loader {
        padding-top: ${({ $size }) => ($size === 'small' ? '2px' : '0')};
        .rs-loader-spin {
          width: 16px;
          height: 16px;
          &:before,
          &:after {
            width: 16px;
            height: 16px;
            border-width: 2px;
          }
        }
      }
    }
  }
`;

const StyledSelect = styled(SelectPicker)<SelectStyleProps>`
  ${selectStyles}
`;
export type Message = {
  noResultsText?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
};
export type Option = {
  value: string;
  label: React.ReactNode;
  group?: string;
};
export interface BaseSelectProps {
  size?: SelectSize;
  width?: string;
  loading?: boolean;
  searchable?: boolean;
  virtualized?: boolean;
  cleanable?: boolean;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  groupBy?: string;
  messages?: Message;
  label?: string;
  placement?: 'topStart' | 'topEnd' | 'bottomStart' | 'bottomEnd';
  container?: HTMLElement | (() => HTMLElement);
}
interface SelectProps extends BaseSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string, event?: React.SyntheticEvent<Element, Event>) => void;
}
const Select: React.FC<SelectProps> = ({
  size = 'small',
  options,
  value,
  onChange,
  width,
  loading = false,
  searchable = false,
  virtualized = false,
  placeholder,
  error = false,
  disabled = false,
  readOnly = false,
  cleanable = true,
  groupBy,
  messages,
  label,
  placement,
  container,
}) => {
  return (
    <StyledSelect
      loading={loading}
      label={label}
      placement={placement}
      value={value}
      $size={size}
      $width={width}
      cleanable={cleanable}
      $error={error}
      disabled={disabled}
      readOnly={readOnly}
      searchable={searchable}
      placeholder={placeholder}
      virtualized={virtualized}
      caretAs={Icons.ArrowFillDown}
      groupBy={groupBy}
      container={container}
      onChange={(v, e) => {
        onChange((v || '') as string, e);
      }}
      data={options}
      locale={messages}
    />
  );
};

export default Select;
